import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { APP_SHORT_LABELS, type AppKey, type Locale } from '@qms360/shared';
import { fetchApps, setMyLocale } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import { NotificationBell } from './NotificationBell.js';
import { t } from '../lib/i18n.js';

/**
 * The per-app top bar (spec §14, ADR-0001).
 *
 * Each app has its OWN top bar with its own identity mark. The switcher is a
 * lightweight way to jump to the other two apps and the Executive Dashboard — a
 * convenience, deliberately not a hub. The client rejected a shared launchpad, and
 * this must not quietly become one.
 *
 * ADR-0014: spacing uses logical properties (ms-*, me-*, ps-*, pe-*) throughout, so
 * this mirrors under dir="rtl" without a single change.
 */

/** Each app reads as related-but-distinct: same design system, own identity mark. */
const APP_MARKS: Record<AppKey | 'executive', { glyph: string; accent: string }> = {
  qaqc: { glyph: '◆', accent: 'var(--ag-purple)' },
  'lesson-learned': { glyph: '◈', accent: 'var(--ag-turquoise)' },
  audit: { glyph: '◉', accent: 'var(--ag-fuchsia)' },
  executive: { glyph: '▣', accent: 'var(--ag-yellow)' },
};

const EXECUTIVE_LABEL = 'Executive 360';

/**
 * `app` may be 'executive': the cross-application view, reached from the
 * switcher by those it opens for. It has no Settings of its own and no inbox —
 * those belong to the applications.
 */
export function AppTopBar({ app }: { app: AppKey | 'executive' }) {
  const { user, signOut, refresh } = useSession();
  const navigate = useNavigate();

  const { data } = useQuery({ queryKey: ['apps'], queryFn: fetchApps });
  const mark = APP_MARKS[app];

  const [changingLanguage, setChangingLanguage] = useState(false);
  const changeLanguage = async (locale: Locale) => {
    setChangingLanguage(true);
    try {
      await setMyLocale(locale);
      // Re-read the session: the server holds the choice, and the app redraws in it.
      await refresh();
    } finally {
      setChangingLanguage(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <>
    {/* First on every page (WCAG 2.4.1; pass 11): past the bar, to the content. */}
    <a
      href="#main"
      className="skip-link"
      onClick={(event) => {
        const main = document.querySelector('main');
        if (!main) return;
        event.preventDefault();
        main.setAttribute('tabindex', '-1');
        main.focus();
      }}
    >
      {t("Skip to the content")}
    </a>
    <header className="bg-ag-black text-content-inverse">
      <div className="mx-auto flex h-14 max-w-7xl items-center gap-4 px-4">
        <Link to={`/${app}`} className="flex items-center gap-2 no-underline">
          <span aria-hidden className="text-xl leading-none" style={{ color: mark.accent }}>
            {mark.glyph}
          </span>
          <span className="font-semibold text-content-inverse">
            {t(app === 'executive' ? EXECUTIVE_LABEL : APP_SHORT_LABELS[app])}
          </span>
        </Link>

        <nav aria-label={t("Application sections")} className="ms-2 flex items-center gap-1 text-sm">
          <Link
            to={`/${app}`}
            className="rounded-sm px-3 py-1.5 text-white/80 no-underline hover:bg-white/10 hover:text-white"
          >
            {t("Home")}
          </Link>
          {app !== 'executive' && (
            <Link
              to={`/${app}/settings`}
              className="rounded-sm px-3 py-1.5 text-white/80 no-underline hover:bg-white/10 hover:text-white"
            >
              {t("Settings")}
            </Link>
          )}
        </nav>

        <div className="ms-auto flex items-center gap-3">
          {app !== 'executive' && <NotificationBell app={app} />}

          {/* The switcher. Apps the user cannot reach are shown but marked, rather
              than hidden — knowing an application exists is not a permission. */}
          <label className="sr-only" htmlFor="app-switcher">
            {t("Switch application")}
          </label>
          <select
            id="app-switcher"
            value={app}
            onChange={(event) => navigate(`/${event.target.value}`)}
            className="rounded-sm border border-white/20 bg-white/10 px-2 py-1.5 text-sm text-white"
          >
            {data?.apps.map((entry) => (
              <option key={entry.key} value={entry.key} className="text-content-primary">
                {entry.shortLabel}
                {entry.hasAccess ? '' : t(" — no access")}
              </option>
            ))}
            {/* Unlike the apps, this is not listed for those it does not open for:
                it is a view across all three, not a fourth application. */}
            {(data?.executive?.hasAccess || app === 'executive') && (
              <option value="executive" className="text-content-primary">
                {t(EXECUTIVE_LABEL)}
              </option>
            )}
          </select>

          <span className="hidden text-sm text-white/70 sm:inline">{user?.displayName}</span>

          {/* Each person's own language (pass 12). The names are written in
              themselves, so either reader can find theirs. */}
          {user && (
            <>
              <label className="sr-only" htmlFor="language">
                {t("Language")}
              </label>
              <select
                id="language"
                value={user.locale}
                disabled={changingLanguage}
                onChange={(event) => void changeLanguage(event.target.value as Locale)}
                className="rounded-sm border border-white/20 bg-transparent px-2 py-1.5 text-sm text-white"
              >
                <option value="en" lang="en" className="text-content-primary">
                  English
                </option>
                <option value="ar" lang="ar" className="text-content-primary">
                  العربية
                </option>
              </select>
            </>
          )}

          {/* Local accounts only: a platform-authenticated user has no QMS360
              password, and offering to change one would be a dead end. */}
          {user?.authSource === 'local' && (
            <Link
              to="/change-password"
              className="rounded-sm px-2 py-1.5 text-sm text-white/80 no-underline hover:bg-white/10 hover:text-white"
            >
              {t("Change password")}
            </Link>
          )}

          <button
            type="button"
            onClick={() => void handleSignOut()}
            className="rounded-sm border border-white/20 px-3 py-1.5 text-sm text-white hover:bg-white/10"
          >
            {t("Sign out")}
          </button>
        </div>
      </div>
    </header>
    </>
  );
}

/** Breadcrumbs back to this app's own home (spec §14). */
export function Breadcrumbs({ app, trail }: { app: AppKey | 'executive'; trail: string[] }) {
  const appLabel = t(app === 'executive' ? EXECUTIVE_LABEL : APP_SHORT_LABELS[app]);
  const here = trail.length ? t(trail[trail.length - 1]) : undefined;
  // Each page names itself in the tab and to a screen reader (WCAG 2.4.2; pass 11).
  useEffect(() => {
    document.title = [here, appLabel, 'QMS360'].filter(Boolean).join(' · ');
  }, [here, appLabel]);

  return (
    <nav aria-label={t("Breadcrumb")} className="mx-auto max-w-7xl px-4 pt-4 text-sm">
      <ol className="flex flex-wrap items-center gap-2 p-0 text-content-secondary">
        <li>
          <Link to={`/${app}`} className="text-ag-purple no-underline hover:underline">
            {t(app === 'executive' ? EXECUTIVE_LABEL : APP_SHORT_LABELS[app])}
          </Link>
        </li>
        {trail.map((crumb) => (
          <li key={crumb} className="flex items-center gap-2">
            <span aria-hidden className="rtl-flip">
              ›
            </span>
            <span>{t(crumb)}</span>
          </li>
        ))}
      </ol>
    </nav>
  );
}
